import { useMutation } from "@tanstack/react-query";
import React, { useCallback, useRef, useState } from "react";
import { AI_GENERATION_MAX_FILE_SIZE } from "common/upload-constants";
import { aiImageService } from "./aiImageService";
import {
  trackAiStudioImageUploadClicked,
  trackAiStudioImageUploadImageSelected,
  trackAiStudioImageUploadImageUploaded
} from "./lib/analytics";
import type { UploadResponse } from "./types";
import { UploadedFilePreview } from "./UploadedFilePreview";
import { UploadIcon } from "./UploadIcon";

type FileUploadProps = {
  onUploadSuccess: (uploadResponse: UploadResponse) => void;
  onUploadError: (error: string) => void;
  isMobile?: boolean;
  clearError: () => void;
};

export const FileUpload = ({
  onUploadSuccess,
  onUploadError,
  clearError,
  isMobile = false
}: FileUploadProps) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const currentFileRef = useRef<File | null>(null);
  const imageMetadataRef = useRef<{
    width: number;
    height: number;
  } | null>(null);

  const uploadMutation = useMutation({
    mutationFn: (file: File) => aiImageService.uploadImage(file),
    onSuccess: (data: UploadResponse) => {
      if (selectedFile && imageMetadataRef.current) {
        trackAiStudioImageUploadImageUploaded({
          fileType: selectedFile.type,
          width: imageMetadataRef.current.width,
          height: imageMetadataRef.current.height,
          fileSize: selectedFile.size
        });
      }
      onUploadSuccess(data);
    },
    onError: (error: Error) => {
      onUploadError(error.message);
    }
  });

  const handleFileSelect = useCallback(
    (file: File | undefined) => {
      if (!file) return;

      // Validate file type - only accept JPG and PNG
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!allowedTypes.includes(file.type.toLowerCase())) {
        onUploadError("Only JPG and PNG files are supported. Please select a different file.");
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > AI_GENERATION_MAX_FILE_SIZE) {
        onUploadError("File size must be less than 10MB");
        return;
      }

      // Clear error
      clearError();

      setSelectedFile(file);
      currentFileRef.current = file;

      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);

      // Get image dimensions for analytics
      const img = new Image();
      img.onload = () => {
        // Only proceed if this is still the current file (prevent race condition)
        if (currentFileRef.current === file) {
          imageMetadataRef.current = {
            width: img.width,
            height: img.height
          };

          trackAiStudioImageUploadImageSelected({
            fileType: file.type,
            width: img.width,
            height: img.height,
            fileSize: file.size
          });
        }
      };
      img.src = url;
    },
    [onUploadError]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      const { files } = e.dataTransfer;
      if (files.length > 0) {
        handleFileSelect(files[0]);
      }
    },
    [handleFileSelect]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      if (files && files.length > 0) {
        handleFileSelect(files[0]);
      }
    },
    [handleFileSelect]
  );

  const handleUpload = () => {
    if (!selectedFile) return;
    uploadMutation.mutate(selectedFile);
  };

  const handleClear = () => {
    setSelectedFile(null);
    currentFileRef.current = null;
    imageMetadataRef.current = null;
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClick = () => {
    trackAiStudioImageUploadClicked();
    fileInputRef.current?.click();
  };

  return (
    <div
      className={`relative transition-all duration-300`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      data-testid="file-upload-container"
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png"
        onChange={handleFileInputChange}
        className="hidden"
        data-testid="file-input"
      />

      {!selectedFile ? (
        <div
          className={`relative rounded transition-all duration-300 ${
            isDragOver ? "ring-2 ring-blue-400 ring-opacity-50" : ""
          }`}
          style={{
            border: "1px dashed #fff",
            background:
              "linear-gradient(#fff 0 0) padding-box, linear-gradient(164.9deg, #BA8DFF 4.95%, #F442F3 46.32%, #344CFF 86%) border-box"
          }}
          onClick={handleClick}
          data-testid="upload-area"
        >
          <div className="flex items-start justify-center space-x-4 p-3 md:space-x-6 md:p-7">
            <div className="-ml-3 mt-1 flex-shrink-0 md:mt-0">
              <UploadIcon />
            </div>

            <div className="text-left">
              <h3 className="mb-0 text-base text-gray-700 md:mb-1 md:text-2xl">
                Or <span className="font-semibold">Upload your own design</span>
                {!isMobile && <span> as a starting point</span>}
              </h3>
              <p className="text-sm text-gray-500 md:text-lg" style={{ margin: 0 }}>
                {isMobile
                  ? "Tap here to upload your design"
                  : "Drag your design here or click to upload"}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <UploadedFilePreview
          selectedFile={selectedFile}
          previewUrl={previewUrl}
          isUploading={uploadMutation.isPending}
          onClear={handleClear}
          onUpload={handleUpload}
        />
      )}
    </div>
  );
};
